import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function RoyalAscotPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (typeof document === 'undefined') return;

    try {
      document.title = 'Royal Ascot Painters Cape Town | Luxury Estate Painting Services | Premium Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Elite painting services in Royal Ascot, Milnerton. Specialist luxury residential painting for exclusive golf estate properties. Expert finishes and premium quality for discerning homeowners.');
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'Royal Ascot painters, luxury estate painting Cape Town, Milnerton painting services');
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/royal-ascot`);
      }
    } catch (err) {
      console.error('Error setting up meta tags:', err);
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/71834cafc4418d32f1bb545ee300a106.jpg"
            alt="Professional Painting Services in Royal Ascot"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Premium <span className="text-yellow-400">Royal Ascot Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Luxury painting services for Royal Ascot's exclusive golf estate properties with premium finishes and exceptional attention to detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
                Get Free Quote
              </Link>
              <Link to="/services" className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200">
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Expert Painting Services for Royal Ascot's Prestigious Golf Estate
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Royal Ascot represents one of Milnerton's most prestigious residential golf estates, characterized by its exclusive properties, championship golf course, sophisticated architectural designs, and upscale lifestyle amenities. This distinguished community demands painting services that match its premium standards, combining technical excellence with aesthetic refinement to maintain and enhance property values in this sought-after estate environment.
              </p>
              <p>
                Our specialized painting services in Royal Ascot cater to the unique requirements of luxury golf estate properties, contemporary family homes, and high-end residential developments. We understand that Royal Ascot homeowners expect nothing less than perfection, which is why our team delivers meticulous attention to detail, premium materials, and flawless finishes that complement the estate's sophisticated character and architectural excellence.
              </p>
              <p>
                Working in Royal Ascot requires an understanding of golf estate living, premium finish requirements, and the expectations of discerning property owners who value quality and professionalism. Our experienced team has completed numerous projects throughout this exclusive estate, earning a reputation for reliability, exceptional quality, and service that meets the exacting standards of Royal Ascot's residential community.
              </p>
              <p>
                From contemporary family homes to luxury villas and modern architectural designs, we provide comprehensive painting solutions that enhance property aesthetics, protect valuable investments, and deliver lasting results. Our commitment to excellence ensures that every Royal Ascot property receives the premium treatment it deserves, with finishes that reflect the estate's prestigious status and sophisticated lifestyle while maintaining harmony with the beautiful golf course surroundings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Royal Ascot Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive luxury painting solutions for golf estate properties
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Luxury Estate Painting',
                description: 'Premium interior and exterior painting for Royal Ascot\'s exclusive homes with designer color consultations, premium materials, and flawless execution that enhances property value.',
                features: ['Designer color matching', 'Premium paint brands', 'Smooth finish guarantee', 'Estate standards compliance', 'Quality assurance', 'Professional execution']
              },
              {
                icon: 'ri-building-4-line',
                title: 'Golf Course View Properties',
                description: 'Specialized painting for homes overlooking the championship golf course with weather-resistant coatings and colors that complement the natural landscape and estate aesthetics.',
                features: ['Landscape-complementary colors', 'UV protection systems', 'Weather resistance', 'Long-lasting durability', 'Fade-resistant finishes', 'Estate harmony']
              },
              {
                icon: 'ri-paint-brush-line',
                title: 'Contemporary Home Painting',
                description: 'Expert painting for modern architectural properties featuring clean lines, sophisticated color schemes, and precision finishing that highlights contemporary design elements.',
                features: ['Modern color palettes', 'Architectural feature painting', 'Precision edge work', 'Contemporary finishes', 'Design enhancement', 'Flawless execution']
              },
              {
                icon: 'ri-home-smile-2-line',
                title: 'Interior Design Painting',
                description: 'Collaborate with interior designers to create stunning living spaces with custom colors, premium textures, and sophisticated finishes that reflect your personal style.',
                features: ['Designer collaboration', 'Custom color creation', 'Texture applications', 'Feature wall expertise', 'Premium materials', 'Detailed craftsmanship']
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Exterior Protection Systems',
                description: 'Advanced exterior painting with weather-resistant coatings designed for Cape Town\'s climate, providing superior protection and maintaining pristine appearances.',
                features: ['Weather protection', 'UV-resistant coatings', 'Moisture barriers', 'Color retention', 'Extended warranty', 'Long-term performance']
              },
              {
                icon: 'ri-tools-line',
                title: 'Estate Maintenance Programs',
                description: 'Ongoing maintenance services to keep Royal Ascot properties looking pristine with scheduled inspections, preventive care, and priority touch-up services.',
                features: ['Annual inspections', 'Preventive maintenance', 'Priority scheduling', 'Touch-up services', 'Long-term care', 'Value preservation']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-yellow-400 text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Understanding Royal Ascot's Unique Painting Requirements
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Estate Standards</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Royal Ascot maintains high aesthetic standards throughout the estate to preserve property values and community character. Our painting services comply with estate guidelines while delivering exceptional quality that enhances individual properties and contributes to the overall prestige of this exclusive golf estate community.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We work closely with homeowners and estate management to ensure all painting projects meet or exceed community standards, utilizing approved color palettes and premium materials that maintain the sophisticated appearance expected throughout Royal Ascot's residential areas.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Premium Quality Expectations</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Royal Ascot homeowners expect exceptional quality, attention to detail, and professional service that matches their property investments. We deliver premium painting solutions using top-tier materials, experienced craftsmen, and rigorous quality control processes that ensure every project exceeds expectations and maintains the estate's prestigious reputation.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our commitment to excellence includes thorough surface preparation, multiple quality inspections, and meticulous finishing work that creates lasting results worthy of Royal Ascot's distinguished residential community and sophisticated property standards.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Climate Considerations</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Milnerton's coastal proximity brings strong winds, salt air influence, intense UV exposure, and seasonal weather variations that require specialized exterior painting systems. We utilize advanced weather-resistant coatings, UV-stable pigments, and moisture management techniques that ensure long-lasting protection and color retention for Royal Ascot properties.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our climate-specific approach includes proper surface sealing, breathable coating systems, and strategic application timing that maximizes paint performance and durability in Royal Ascot's environmental conditions, protecting your investment for years to come.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Minimal Disruption</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We understand that Royal Ascot residents value their privacy, comfort, and daily routines. Our professional approach includes flexible scheduling, efficient project management, and respectful work practices that minimize disruption while delivering exceptional results within agreed timelines.
              </p>
              <p className="text-gray-700 leading-relaxed">
                From careful site protection to thorough daily cleanup, we maintain the highest standards of professionalism and consideration, ensuring your painting project proceeds smoothly without compromising your lifestyle or property enjoyment throughout the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Royal Ascot Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for golf estate properties
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Estate Property Consultation',
                description: 'Comprehensive on-site assessment of your Royal Ascot property with detailed discussion of your vision, estate requirements, and expectations for the project.'
              },
              {
                step: '02',
                title: 'Design & Planning',
                description: 'Professional color consultation, material selection, and project planning tailored to your property\'s architecture, estate guidelines, and your aesthetic preferences.'
              },
              {
                step: '03',
                title: 'Detailed Quotation',
                description: 'Transparent, itemized proposal outlining scope, timeline, premium materials, and costs with no hidden fees or surprises throughout the project.'
              },
              {
                step: '04',
                title: 'Surface Preparation',
                description: 'Meticulous preparation including cleaning, repairs, priming, and protection to ensure optimal paint adhesion and flawless final results.'
              },
              {
                step: '05',
                title: 'Expert Application',
                description: 'Professional painting by experienced craftsmen using premium materials and proven techniques that deliver exceptional quality and durability.'
              },
              {
                step: '06',
                title: 'Quality Assurance',
                description: 'Thorough inspection, touch-ups, and final walkthrough to ensure complete satisfaction and guarantee that every detail meets our exacting standards.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl font-bold text-yellow-400 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Royal Ascot Homeowners Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Premium Quality Guarantee',
                description: 'We use only the finest paints and materials from leading brands, combined with expert craftsmanship that ensures exceptional results and long-lasting beauty for your Royal Ascot property.'
              },
              {
                icon: 'ri-team-line',
                title: 'Experienced Professionals',
                description: 'Our skilled team brings years of experience working on luxury golf estate properties, understanding the unique requirements and high standards expected in this prestigious community.'
              },
              {
                icon: 'ri-time-line',
                title: 'Reliable & Punctual',
                description: 'We respect your time and schedule, arriving promptly, working efficiently, and completing projects within agreed timelines while maintaining the highest quality standards throughout.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Exceptional Service',
                description: 'From initial consultation to final inspection, we provide professional, courteous service that prioritizes your satisfaction, addresses your concerns, and exceeds your expectations at every stage.'
              },
              {
                icon: 'ri-paint-brush-line',
                title: 'Attention to Detail',
                description: 'Every project receives meticulous attention to detail, from precise edge work to smooth finishes, ensuring flawless results that enhance your property\'s beauty and value.'
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Complete Satisfaction',
                description: 'Your complete satisfaction is our ultimate goal. We stand behind our work with comprehensive guarantees and remain committed to delivering results that make you proud of your Royal Ascot home.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Recent Royal Ascot Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our premium painting projects in Royal Ascot golf estate
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Contemporary Golf Estate Home',
                location: 'Royal Ascot Fairway',
                description: 'Complete interior and exterior transformation of a modern 4-bedroom family home overlooking the golf course featuring custom color palette, premium finishes, and architectural feature painting that enhanced the property\'s contemporary design and estate appeal.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20home%20in%20Royal%20Ascot%20golf%20estate%20Milnerton%20with%20contemporary%20architecture%2C%20freshly%20painted%20exterior%2C%20golf%20course%20views%2C%20manicured%20garden%2C%20sophisticated%20design%2C%20professional%20finish&width=800&height=600&seq=royalascot-project-001&orientation=landscape'
              },
              {
                title: 'Luxury Family Villa',
                location: 'Royal Ascot Estate',
                description: 'Professional painting for a prestigious family villa including coordinated interior and exterior color schemes, premium weather-resistant coatings, and meticulous attention to detail that elevated the property\'s luxury appeal and market value.',
                image: 'https://readdy.ai/api/search-image?query=Luxury%20family%20villa%20in%20Royal%20Ascot%20Milnerton%20with%20fresh%20paint%2C%20elegant%20exterior%2C%20modern%20design%2C%20golf%20estate%20setting%2C%20professional%20painting%2C%20upscale%20residential%20property&width=800&height=600&seq=royalascot-project-002&orientation=landscape'
              },
              {
                title: 'Architectural Showcase Home',
                location: 'Royal Ascot Premium Location',
                description: 'Specialized painting for an award-winning contemporary residence featuring complex architectural elements, sophisticated color contrasts, and precision finishing that highlighted the property\'s unique design features and artistic vision within the estate.',
                image: 'https://readdy.ai/api/search-image?query=Architectural%20showcase%20home%20in%20Royal%20Ascot%20golf%20estate%20with%20unique%20modern%20design%2C%20bold%20contemporary%20colors%2C%20professional%20painting%2C%20luxury%20property%2C%20artistic%20architecture%2C%20Milnerton&width=800&height=600&seq=royalascot-project-003&orientation=landscape'
              },
              {
                title: 'Estate Renovation Project',
                location: 'Royal Ascot Residential Area',
                description: 'Comprehensive exterior and interior renovation painting for an established Royal Ascot property including surface restoration, color modernization, and protective coating systems that revitalized the home while maintaining estate harmony.',
                image: 'https://readdy.ai/api/search-image?query=Renovated%20estate%20home%20in%20Royal%20Ascot%20Milnerton%20with%20refreshed%20paint%2C%20elegant%20exterior%2C%20modern%20color%20scheme%2C%20golf%20course%20setting%2C%20professional%20restoration%2C%20upscale%20property&width=800&height=600&seq=royalascot-project-004&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="w-full h-80">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            What Royal Ascot Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Robert & Michelle Thompson',
                location: 'Royal Ascot Estate',
                text: 'Exceptional quality and professionalism from start to finish. Our home looks absolutely stunning, and the attention to detail was remarkable. The team understood the estate standards perfectly. Highly recommend their services!'
              },
              {
                name: 'David Harrison',
                location: 'Royal Ascot Golf View',
                text: 'The team transformed our property with beautiful results that exceeded our expectations. Professional, reliable, and meticulous in their work. The perfect choice for luxury home painting in Royal Ascot.'
              },
              {
                name: 'Susan van Rensburg',
                location: 'Royal Ascot Residence',
                text: 'Outstanding service and beautiful finishes throughout our home. The team was respectful, efficient, and delivered flawless results. Our property has never looked better, and we couldn\'t be happier with the investment.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">&quot;{testimonial.text}&quot;</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Transform Your Royal Ascot Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us today for a free consultation and discover how our premium painting services can enhance your golf estate home's beauty and value.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
            <a href="tel:+27123456789" className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}